import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AccordianComponent } from '../../../components/accordian/Accordian.components'
import {
  IonButton,
  IonButtons,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemSliding,
  IonLabel,
  IonRow,
  useIonViewWillLeave,
} from '@ionic/react'
import { addCircleOutline, trashOutline } from 'ionicons/icons'
import { useDispatch, useSelector } from 'react-redux'
import { getSceneDescriptionOptionsSelector } from '../../options/selectors'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import { IOption } from '../../../common/common.types'
import SInput from '../../../components/stringInput/StringInput.component'
import {
  addNewAgency,
  cancelFirstOnSceneUpdate,
  removeAgency,
  setOtherAgencyFirstOnScene,
  setOtherAgencyFirstOnScenePNNV,
  setOtherAgencyName,
  setOtherAgencyNumber,
  setOtherAgencyType,
  updateFirstOnScene,
} from '../store/sceneDescription.actions'
import { getSceneDescriptionSelector } from '../selectors'
import { IOtherAgency } from '../store/sceneDescription.types'
import { showAlert } from '../../../components/alert/store/alert.actions'
import { IAlertState } from '../../../components/alert/store/alert.types'
import { otherAgencyIsNonEmpty } from '../utils/sceneDescription.util'
import { SceneDescriptionConstants } from '../store/sceneDescription.constants'
import Validation from '../../../components/validation/Validation.component'

const OtherAgencies: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { otherAgencies, showFirstOnSceneMessage } = useSelector(getSceneDescriptionSelector)
  const { otherAgencyNameOptions, otherAgencyTypeOptions, firstOnSceneOptions } = useSelector(
    getSceneDescriptionOptionsSelector
  )

  const [openAccordian, setOpenAccordian] = useState(false)

  useIonViewWillLeave(() => {
    setOpenAccordian(false)
  })

  const handleOpenAccordian = (value: boolean) => {
    // If first time opening the accordian, add an initial agency
    if (!otherAgencies.length && value) {
      dispatch(addNewAgency())
    }

    setOpenAccordian(value)
  }

  const shouldShowCheckmark = (): boolean => {
    return otherAgencies.some((otherAgency) => otherAgencyIsNonEmpty(otherAgency))
  }

  const setAgencyName = (option: IOption, key: string) => {
    dispatch(setOtherAgencyName(option, key))
  }

  const setAgencyType = (option: IOption, key: string) => {
    dispatch(setOtherAgencyType(option, key))
  }

  const setAgencyNumber = (e: CustomEvent, key: string) => {
    e.preventDefault()
    dispatch(setOtherAgencyNumber(e.detail.value, key))
  }

  const setFirstOnScene = (option: IOption, key: string) => {
    dispatch(setOtherAgencyFirstOnScene(option, key))
  }

  const setFirstOnScenePNNV = (option: IOption, key: string) => {
    dispatch(setOtherAgencyFirstOnScenePNNV(option, key))
  }

  const onAddOtherAgency = () => {
    dispatch(addNewAgency())
  }

  const clearAgency = (key: string) => {
    dispatch(setOtherAgencyName(null, key))
    dispatch(setOtherAgencyType(null, key))
    dispatch(setOtherAgencyNumber('', key))
    dispatch(setOtherAgencyFirstOnScene(null, key))
    dispatch(setOtherAgencyFirstOnScenePNNV(null, key))
  }

  const deleteOtherAgency = (key: string) => {
    if (otherAgencies.length > 1) {
      dispatch(removeAgency(key))
    } else {
      clearAgency(key)
    }
  }

  const handleFirstOnSceneUpdate = () => {
    dispatch(updateFirstOnScene())
  }

  const handleFirstOnSceneUpdateCancel = () => {
    dispatch(cancelFirstOnSceneUpdate())
  }

  const handleFirstOnSceneMessage = () => {
    const firstOnSceneAlert: IAlertState = {
      message: t('sceneDescription.otherAgencies.firstOnSceneWarning'),
      buttons: [
        {
          text: t('common.buttons.Cancel'),
          handler: handleFirstOnSceneUpdateCancel,
        },
        {
          text: t('common.buttons.Ok'),
          handler: handleFirstOnSceneUpdate,
        },
      ],
    }

    dispatch(showAlert(firstOnSceneAlert))
  }

  useEffect(() => {
    if (showFirstOnSceneMessage) handleFirstOnSceneMessage()
  }, [showFirstOnSceneMessage])

  const OtherAgenciesContent = (
    <>
      {otherAgencies.length > 0 &&
        otherAgencies.map((otherAgency: IOtherAgency, index: number) => {
          return (
            <span key={index}>
              <IonRow id={index.toString()}>
                <IonCol>
                  <IonItem lines='none'>
                    <div className='circle-container'>{index + 1}</div>
                  </IonItem>
                </IonCol>
                <IonCol>
                  <IonButtons className='ion-float-right '>
                    <IonButton data-testid='Delete-OtherAgencies' onClick={() => deleteOtherAgency(otherAgency.key)}>
                      <IonIcon icon={trashOutline} />
                    </IonButton>
                  </IonButtons>
                </IonCol>
              </IonRow>
              <IonItemSliding key={otherAgency.key}>
                <IonGrid className='no-padding'>
                  <IonRow>
                    <IonCol className='picklist'>
                      <SingleSelect
                        dataTestID='OtherAgencyUnitName'
                        selectedOption={otherAgency.agencyName}
                        listHeader={t('sceneDescription.otherAgencies.agencyName')}
                        options={otherAgencyNameOptions}
                        action={setAgencyName}
                        label={t('sceneDescription.otherAgencies.agencyName')}
                        dispatchData={otherAgency.key}
                      />
                    </IonCol>
                  </IonRow>
                  <hr className='field-divider' />
                  <IonRow>
                    <IonCol className='picklist'>
                      <SingleSelect
                        dataTestID='OtherAgencyUnitType'
                        selectedOption={otherAgency.agencyType}
                        listHeader={t('sceneDescription.otherAgencies.agencyType')}
                        options={otherAgencyTypeOptions}
                        action={setAgencyType}
                        label={t('sceneDescription.otherAgencies.agencyType')}
                        dispatchData={otherAgency.key}
                      />
                    </IonCol>
                  </IonRow>
                  <hr className='field-divider' />
                  <IonRow>
                    <IonCol className='picklist'>
                      <SInput
                        label={t('sceneDescription.otherAgencies.agencyNumber')}
                        dataTestID='AptSuiteRoom'
                        inputValue={otherAgency.agencyNumber || ''}
                        onChangeAction={setAgencyNumber}
                        dispatchData={otherAgency.key}
                      />
                      <Validation
                        id={SceneDescriptionConstants.SET_OTHER_AGENCY_NUMBER}
                        cardinalityKey={otherAgency.key}
                      />
                    </IonCol>
                  </IonRow>
                  <hr className='field-divider' />
                  <IonRow>
                    <IonCol className='picklist'>
                      <SingleSelect
                        dataTestID='OtherAgencyFirstOnScene'
                        selectedOption={otherAgency.firstOnScene}
                        listHeader={t('sceneDescription.otherAgencies.firstOnScene')}
                        options={firstOnSceneOptions}
                        action={setFirstOnScene}
                        label={t('sceneDescription.otherAgencies.firstOnScene')}
                        dispatchData={otherAgency.key}
                        selectedPNNVOption={otherAgency.firstOnScenePNNV}
                        pnnvAction={setFirstOnScenePNNV}
                      />
                    </IonCol>
                  </IonRow>
                  <hr className='field-divider' />
                </IonGrid>
              </IonItemSliding>
            </span>
          )
        })}
      <IonRow>
        <IonCol>
          <IonButtons className='ion-float-right '>
            <IonButton
              data-testid='New'
              className='transform-none'
              fill='clear'
              color='primary'
              onClick={() => onAddOtherAgency()}>
              <IonIcon icon={addCircleOutline} />
              <IonLabel>{t('common.buttons.New')}</IonLabel>
            </IonButton>
          </IonButtons>
        </IonCol>
      </IonRow>
    </>
  )

  return (
    <AccordianComponent
      dataTestId={'OtherAgencies'}
      heading={t('sceneDescription.otherAgencies.title')}
      content={OtherAgenciesContent}
      action={(value) => handleOpenAccordian(value)}
      openAccordion={openAccordian}
      isCheckMark={shouldShowCheckmark()}
    />
  )
}

export default OtherAgencies
